import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import MarqiLogo from './MarqiLogo';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const navigation = [
    {
      name: 'Ops',
      href: '/ops',
      dropdownItems: [
        { name: 'Ad Operations', href: '/ops/ad-operations', description: 'Campaign Management' },
        { name: 'Analytics', href: '/ops/analytics', description: 'Data & Attribution' },
        { name: 'Pricing', href: '/ops/pricing', description: 'Operations Packages' }
      ]
    },
    {
      name: 'Cast',
      href: '/cast',
      dropdownItems: [
        { name: 'Overview', href: '/cast', description: 'CTV for Agencies' },
        { name: 'AI Video Creative', href: '/cast/creative', description: 'Broadcast Spots in Minutes' },
        { name: 'Targeting & Retargeting', href: '/cast/targeting', description: 'Hyperlocal & Second Screen' },
        { name: 'Measurement', href: '/cast/measurement', description: 'Proof of ROI' },
        { name: 'Inventory', href: '/cast/inventory', description: 'Premium CTV Inventory' },
        { name: 'Pricing', href: '/cast/pricing', description: 'How CTV Pricing Works' }
      ]
    },
    {
      name: 'Rank',
      href: '/rank',
      dropdownItems: [
        { name: 'Overview', href: '/rank', description: 'AI Search Visibility' },
        { name: 'GEO', href: '/rank/geo', description: 'Generative Engine Optimisation' },
        { name: 'AEO', href: '/rank/aeo', description: 'Answer Engine Optimisation' },
        { name: 'ChatGPT Ads', href: '/rank/chatgpt-ads', description: 'Paid AI Placements — Now Live' },
        { name: 'Measurement', href: '/rank/measurement', description: 'AI Visibility Reporting' },
        { name: 'Pricing', href: '/rank/pricing', description: 'Transparent Per-Client Pricing' }
      ]
    },
    { name: 'About', href: '/about' },
    { name: 'Blog', href: '/blog' }
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <header className="sticky top-0 z-50 bg-[var(--bg-primary)] border-b border-[var(--border-subtle)] backdrop-blur-md">
      <div className="container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <MarqiLogo size="default" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative group"
                onMouseEnter={() => item.dropdownItems && setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg font-semibold text-lg transition-all ${
                    isActive(item.href)
                      ? 'text-[var(--accent-primary)] bg-[var(--accent-bg)]'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                  }`}
                >
                  <span>{item.name}</span>
                  {item.dropdownItems && <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />}
                </Link>

                {/* Dropdown Menu */}
                {item.dropdownItems && activeDropdown === item.name && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-lg shadow-2xl py-3 z-50 animate-fade-in">
                    <div className="px-4 py-2 border-b border-[var(--border-subtle)] mb-2">
                      <span className="text-xs font-semibold text-[var(--accent-primary)] uppercase tracking-wider">
                        {item.name} Services
                      </span>
                    </div>
                    {item.dropdownItems.map((dropdownItem) => (
                      <Link
                        key={dropdownItem.name}
                        to={dropdownItem.href}
                        className="flex items-center px-4 py-3 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors group/item"
                      >
                        <div className="w-2 h-2 bg-[var(--accent-primary)] rounded-full mr-3 opacity-60 group-hover/item:opacity-100 transition-opacity"></div>
                        <div>
                          <div className="font-medium">{dropdownItem.name}</div>
                          <div className="text-xs text-[var(--text-muted)] mt-1">
                            {dropdownItem.description}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover/item:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                    <div className="border-t border-[var(--border-subtle)] mt-2 pt-2 px-4">
                      <Link
                        to="/contact"
                        className="block text-center py-2 px-4 bg-[var(--accent-primary)] text-[var(--bg-primary)] rounded-lg text-sm font-medium hover:bg-[var(--accent-hover)] transition-colors"
                      >
                        Get Started
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/contact" className="btn-primary">
              Book a Session
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-[var(--border-subtle)]">
            <nav className="space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`block px-4 py-2 rounded-lg font-semibold text-lg transition-colors ${
                      isActive(item.href)
                        ? 'text-[var(--accent-primary)] bg-[var(--accent-bg)]'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.dropdownItems && (
                    <div className="ml-4 mt-2 space-y-1">
                      {item.dropdownItems.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className="block px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {dropdownItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 px-4">
                <Link
                  to="/contact"
                  className="btn-primary w-full"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Book a Session
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
